import axios from 'axios';
import { userService } from './userService';
import { handleLogout } from '../utils/logout';
import { secureLocalStorage } from '../utils/crypto';
import { config } from '../Constants/Config';
import { getTenantId, initializeTenant } from '../services/tenantService';

let isRefreshing = false;
let failedQueue = [];

const CACHE_TIMESTAMP_KEY = 'json_cache_timestamp';

const getCacheTimestamp = () => {
  let timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);
  if (!timestamp) {
    timestamp = Date.now().toString();
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, timestamp);
  }
  return timestamp;
};

const clearCacheTimestamp = () => {
  sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const setupInterceptors = (instance) => {
  // Authorization and JSON Cache Interceptor
  instance.interceptors.request.use(async (config) => {
    // Add authorization header if token exists
    const idToken = secureLocalStorage.getItem('idToken');
    if (idToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    // JSON Cache Interceptor - Add timestamp to JSON requests
    if (
      config.method === 'get' &&
      typeof config.url === 'string' &&
      config.url.endsWith('.json')
    ) {
      const separator = config.url.includes('?') ? '&' : '?';
      config.url = `${config.url}${separator}_ts=${getCacheTimestamp()}`;
    }
    return config;
  }, error => Promise.reject(error));

  // Response Error Interceptor - Handle 401 errors with token refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && originalRequest._skipLogoutOn401) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = secureLocalStorage.getItem('refreshToken');
          const username = secureLocalStorage.getItem('username');
          
          if (!refreshToken || !username) {
            throw new Error('No refresh token or username found');
          }

          const response = await userService.refreshAccessToken(refreshToken, username);
          const newToken = response.idToken;
          
          instance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          processQueue(null, newToken);
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Não redireciona para logout em requisições de login (ex.: SSO 401 = falha de credenciais)
          if (!originalRequest._skipLogoutOn401) {
            handleLogout();
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

// Create a pre-configured Axios instance with tenant-aware features
const createTenantAwareInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    timeout: 10000,
  });
  
  // Add interceptor to ensure tenant ID is always present
  instance.interceptors.request.use(async (config) => {
    let tenantId = getTenantId();
    
    // If tenant ID is not available, try to initialize it
    if (!tenantId) {
      try {
        await initializeTenant();
        tenantId = getTenantId();
      } catch (error) {
        console.error('Failed to initialize tenant during API call:', error);
      }
    }
    
    if (tenantId) {
      config.headers = config.headers || {};
      config.headers['X-Tenant-Id'] = tenantId;
    }
    
    return config;
  });
  
  setupInterceptors(instance);
  
  return instance;
};

// Default instance for S3 or other services
export const instance = createTenantAwareInstance(config.S3_BASE_URL);

export { setupInterceptors, createTenantAwareInstance };

// Export the clear function to be used during logout
export const clearJsonCache = clearCacheTimestamp; 
